import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Paper, Tab, Tabs } from '@mui/material';

import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import {
  Client,
  ClientAddress,
  ClientAdmin,
  ClientContact,
} from '../../../data/models/client.model';
import {
  Program,
  ServicePlan,
  ServicePlanServiceArea,
} from '../../../data/models/client-program.model';

import {
  fetchUserRolesAsync,
  selectUserRoles,
} from '../../../data/security.slice';
import { selectUserCategoryTypes } from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as lookupSlice from '../../../data/lookup.slice';

import ClientProfile from './ClientProfile';
import ClientContacts from './ClientContacts';
import ClientAdmins from './ClientAdmins';
import ClientAddresses from './ClientAddresses';
import ClientPrograms from './ClientPrograms';
import ClientProfileDialog from './ClientProfileDialog';
import AddressDialog from './AddressDialog';
import ConfirmationDialog from '../../ConfirmationDialog';
import ContactDialog from './ContactDialog';
import AdminDialog from './AdminDialog';
import ProgramDialog from './ProgramDialog';
import ClientServicePlans from './ClientServicePlans';
import ServicePlanDialog from './ServicePlanDialog';
import ClientPurposeCodes from './ClientPurposeCodes';
import PurposeCodeDialog from './PurposeCodeDialog';
import ServiceAreaDialog from './ServiceAreaDialog';
import ClientServiceAreasDeprecated from './ClientServiceAreasDeprecated';
import TabPanel from '../../TabPanel';
import ClientPricing from './ClientPricing';
import ActivateDialog from '../ActivateDialog';
import ClientTransportationBenefits from './transportation-benefit/ClientTransportationBenefits';
import { TransportationBenefit } from '../../../data/models/transportation-benefit.model';
import TransportationBenefitDialog from './transportation-benefit/TransportationBenefitDialog';
import ClientLocations from './locations/ClientLocations';
import ClientPreferences from './preferences/ClientPreferences';
import { ClientPurposeCode } from '../../../data/models/purpose-code-model.model';

const PREFIX = 'ClientDetails';

const classes = {
  container: `${PREFIX}-container`,
  tab: `${PREFIX}-tab`,
};

const StyledBox = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    margin: theme.spacing(2),
  },
  [`& .${classes.tab}`]: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
}));

interface ClientDetailsProps {
  client?: Client;
  readonly: boolean;
}

export default function ClientDetails({
  client,
  readonly,
}: ClientDetailsProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const clientBC = useRef(client?.clientBC);

  const addresses = useAppSelector(clientSlice.selectAddresses);
  const contacts = useAppSelector(clientSlice.selectContacts);
  const admins = useAppSelector(clientSlice.selectAdmins);
  const purposeCodes = useAppSelector(clientSlice.selectPurposeCodes);
  const serviceAreas = useAppSelector(clientSlice.selectServiceAreas);
  const transportationBenefits = useAppSelector(
    clientSlice.selectTransportationBenefits,
  );
  const pricingModels = useAppSelector(
    accountingSlice.selectActivePricingModels,
  );
  const servicePlanList = useAppSelector(clientSlice.selectServicePlanList);
  const programs = useAppSelector(clientSlice.selectPrograms);
  const preferences = useAppSelector(clientSlice.selectClientSettings);
  const [servicePlanSelections, setServicePlanSelections] = React.useState<{
    [id: string]: boolean;
  }>({});
  const userCategoryTypes = useAppSelector(selectUserCategoryTypes);
  const userRoles = useAppSelector(selectUserRoles);

  const userCategoryTypeIDs = useMemo(() => {
    return userCategoryTypes
      .filter((type) => type.userCategoryTypeBC === 'CLIENT')
      .map((type) => type.userCategoryTypeID);
  }, [userCategoryTypes]);

  const userRoleIDs = useMemo(() => {
    return userRoles
      .filter(
        (userRole) =>
          userRole.userRoleBC === 'clientportal.client.admin' &&
          userRole.userRoleID !== undefined,
      )
      .map((userRole) => userRole.userRoleID)
      .filter((id): id is number => id !== undefined);
  }, [userRoles]);

  const [clientProfileDialogOpen, setClientProfileDialogOpen] =
    React.useState(false);
  const [editingAddress, setEditingAddress] = React.useState<ClientAddress>();
  const [editingContact, setEditingContact] = React.useState<ClientContact>();
  const [editingServiceArea, setEditingServiceArea] =
    React.useState<ServicePlanServiceArea>();
  const [editingServicePlan, setEditingServicePlan] =
    React.useState<ServicePlan>();
  const [editingProgram, setEditingProgram] = React.useState<Program>();
  const [editingTransportationBenefit, setEditingTransportationBenefit] =
    React.useState<TransportationBenefit>();
  const [addressDialogOpen, setAddressDialogOpen] = React.useState(false);
  const [contactDialogOpen, setContactDialogOpen] = React.useState(false);
  const [adminDialogOpen, setAdminDialogOpen] = React.useState(false);
  const [purposeCodeDialogOpen, setPurposeCodeDialogOpen] =
    React.useState(false);
  const [serviceAreaDialogOpen, setServiceAreaDialogOpen] =
    React.useState(false);
  const [transportationBenefitDialogOpen, setTransportationBenefitDialogOpen] =
    React.useState(false);
  const [servicePlanDialogOpen, setServicePlanDialogOpen] =
    React.useState(false);
  const [programDialogOpen, setProgramDialogOpen] = React.useState(false);
  const [confirmDialogTitle, setConfirmDialogTitle] = React.useState('');
  const [confirmDialogMessage, setConfirmDialogMessage] = React.useState('');
  const [confirmDialogPostiveFn, setConfirmDialogPostiveFn] = React.useState<
    () => void
  >(() => null);
  const [confirmDialogNegativeFn, setConfirmDialogNegativeFn] = React.useState<
    () => void
  >(() => null);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [isDeactivate, setIsDeactivate] = React.useState(false);
  const [activateDialogTitle, setActivateDialogTitle] = React.useState('');
  const [activateDialogMessage, setActivateDialogMessage] = React.useState('');
  const [activateDialogPostiveFn, setActivateDialogPostiveFn] = React.useState<
    () => void
  >(() => null);
  const [activateDialogNegativeFn, setActivateDialogNegativeFn] =
    React.useState<() => void>(() => null);
  const [activateDialogOpen, setActivateDialogOpen] = React.useState(false);

  const [draggedPurposeCode, setDraggedPurposeCode] =
    React.useState<ClientPurposeCode>();
  const [draggedServiceArea, setDraggedServiceArea] =
    React.useState<ServicePlanServiceArea>();
  const [draggedTransportationBenefit, setDraggedTransportationBenefit] =
    React.useState<TransportationBenefit>();
  const [draggedServicePlan, setDraggedServicePlan] =
    React.useState<ServicePlan>();
  const [droppedServicePlans, setDroppedServicePlans] = React.useState<
    ServicePlan[]
  >([]);
  const [droppedProgram, setDroppedProgram] = React.useState<Program>();
  const [adminUserPage, setAdminUserPage] = React.useState(1);

  const [tabIndex, setTabIndex] = React.useState(0);

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const editProfile = (client: Client | null) => {
    setClientProfileDialogOpen(true);
  };

  const fetchMoreServicePlans = (token: string) => {
    dispatch(
      clientSlice.fetchClientServicePlansAsync(client?.clientBC!, token),
    );
  };

  const updateClientEffectiveDates = (
    bc: string,
    start?: string,
    end?: string,
  ) => {
    dispatch(clientSlice.updateClientEffectiveDatesAsync(bc, start, end));
  };

  const activateClient = (client: Client) => {
    const confirm = (start?: string, end?: string) => {
      updateClientEffectiveDates(client.clientBC, start, end);
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(false);
    setActivateDialogTitle(t('client.confirmActivateClientTitle'));
    setActivateDialogMessage(t('client.confirmActivateClientMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const deactivateClient = (client: Client) => {
    const confirm = (start?: string, end?: string) => {
      updateClientEffectiveDates(client.clientBC, start, end);
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(true);
    setActivateDialogTitle(t('client.confirmDeactivateClientTitle'));
    setActivateDialogMessage(t('client.confirmDeactivateClientMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const addAddress = () => {
    setEditingAddress(undefined);
    setAddressDialogOpen(true);
  };

  const editAddress = (address: ClientAddress) => {
    setEditingAddress(address);
    setAddressDialogOpen(true);
  };

  const activateAddress = (address: ClientAddress) => {
    const confirm = () => {
      dispatch(
        clientSlice.activateClientAddressAsync(client!.clientBC, address),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmActivateAddressTitle'));
    setConfirmDialogMessage(t('client.confirmActivateAddressMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deactivateAddress = (address: ClientAddress) => {
    const confirm = () => {
      dispatch(
        clientSlice.deactivateClientAddressAsync(client!.clientBC, address),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeactivateAddressTitle'));
    setConfirmDialogMessage(t('client.confirmDeactivateAddressMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deleteAddress = (address: ClientAddress) => {
    const confirm = () => {
      dispatch(
        clientSlice.deleteClientAddressAsync(client!.clientBC, address!),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeleteAddressTitle'));
    setConfirmDialogMessage(t('client.confirmDeleteAddressMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const addContact = () => {
    setEditingContact(undefined);
    setContactDialogOpen(true);
  };

  const editContact = (contact: ClientContact) => {
    setEditingContact(contact);
    setContactDialogOpen(true);
  };

  const activateContact = (contact: ClientContact) => {
    const confirm = () => {
      dispatch(
        clientSlice.activateClientContactAsync(client!.clientBC, contact),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmActivateContactTitle'));
    setConfirmDialogMessage(t('client.confirmActivateContactMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deactivateContact = (contact: ClientContact) => {
    const confirm = () => {
      dispatch(
        clientSlice.deactivateClientContactAsync(client!.clientBC, contact),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeactivateContactTitle'));
    setConfirmDialogMessage(t('client.confirmDeactivateContactMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deleteContact = (contact: ClientContact) => {
    const confirm = () => {
      dispatch(
        clientSlice.deleteClientContactAsync(client!.clientBC, contact!),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeleteContactTitle'));
    setConfirmDialogMessage(t('client.confirmDeleteContactMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const closeContactDialog = () => {
    setContactDialogOpen(false);
  };

  // Admin management functions
  const addAdmin = () => {
    setAdminDialogOpen(true);
  };

  const resendInvite = (admin: ClientAdmin) => {
    dispatch(
      clientSlice.resendClientAdminInviteAsync(
        client!.clientBC,
        admin.userAccountID!,
      ),
    );
  };

  const deleteAdmin = (admin: ClientAdmin) => {
    const confirm = () => {
      dispatch(
        clientSlice.deleteClientAdminAsync(
          client!.clientBC,
          admin.userAccountID!,
        ),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeleteAdminTitle'));
    setConfirmDialogMessage(t('client.confirmDeleteAdminMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const closeAdminDialog = () => {
    setAdminDialogOpen(false);
  };

  const addPurposeCode = () => {
    setPurposeCodeDialogOpen(true);
  };

  const activatePurposeCode = (purposeCode: ClientPurposeCode) => {
    const confirm = () => {
      dispatch(
        clientSlice.activateClientPurposeCodeAsync(
          client!.clientBC,
          purposeCode!,
        ),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmActivatePurposeCodeTitle'));
    setConfirmDialogMessage(t('client.confirmActivatePurposeCodeMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deactivatePurposeCode = (purposeCode: ClientPurposeCode) => {
    const confirm = () => {
      dispatch(
        clientSlice.deactivateClientPurposeCodeAsync(
          client!.clientBC,
          purposeCode!,
        ),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeactivatePurposeCodeTitle'));
    setConfirmDialogMessage(t('client.confirmDeactivatePurposeCodeMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deletePurposeCode = (purposeCode: ClientPurposeCode) => {
    const confirm = () => {
      dispatch(
        clientSlice.deleteClientPurposeCodeAsync(
          client!.clientBC,
          purposeCode!,
        ),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeletePurposeCodeTitle'));
    setConfirmDialogMessage(t('client.confirmDeletePurposeCodeMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const addServiceArea = () => {
    setEditingServiceArea(undefined);
    setServiceAreaDialogOpen(true);
  };

  const activateServiceArea = (serviceArea: ServicePlanServiceArea) => {
    const confirm = () => {
      dispatch(
        clientSlice.activateClientServiceAreaAsync(
          client!.clientBC,
          serviceArea,
        ),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmActivateServiceAreaTitle'));
    setConfirmDialogMessage(t('client.confirmActivateServiceAreaMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deactivateServiceArea = (serviceArea: ServicePlanServiceArea) => {
    const confirm = () => {
      dispatch(
        clientSlice.deactivateClientServiceAreaAsync(
          client!.clientBC,
          serviceArea,
        ),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeactivateServiceAreaTitle'));
    setConfirmDialogMessage(t('client.confirmDeactivateServiceAreaMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const editServiceArea = (serviceArea: ServicePlanServiceArea) => {
    setEditingServiceArea(serviceArea);
    setServiceAreaDialogOpen(true);
  };

  const deleteServiceArea = (serviceArea: ServicePlanServiceArea) => {
    const confirm = () => {
      dispatch(
        clientSlice.deleteClientServiceAreaAsync(client!.clientBC, serviceArea),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeleteServiceAreaTitle'));
    setConfirmDialogMessage(t('client.confirmDeleteServiceAreaMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const addTransportationBenefit = () => {
    setEditingTransportationBenefit(undefined);
    setTransportationBenefitDialogOpen(true);
  };

  const editTransportationBenefit = (
    evt: any,
    transportationBenefit: TransportationBenefit,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();
    setEditingTransportationBenefit(transportationBenefit);
    setTransportationBenefitDialogOpen(true);
  };

  const updateTransportationBenefitEffectiveDates = (
    clientBC: string,
    transportationBenefitUUID: string,
    start?: string,
    end?: string,
  ) => {
    dispatch(
      clientSlice.updateTransportationBenefitEffectiveDatesAsync(
        clientBC,
        transportationBenefitUUID,
        start,
        end,
      ),
    );
  };

  const activateTransportationBenefit = (
    evt: any,
    transportationBenefit: TransportationBenefit,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();
    const confirm = (start?: string, end?: string) => {
      updateTransportationBenefitEffectiveDates(
        client!.clientBC,
        transportationBenefit.transportationBenefitUUID!,
        start,
        end,
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(false);
    setActivateDialogTitle(t('client.setTransportationBenefitDatesTitle'));
    setActivateDialogMessage(t('client.setTransportationBenefitDatesMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const deactivateTransportationBenefit = (
    evt: any,
    transportationBenefit: TransportationBenefit,
  ) => {
    // evt.preventDefault();
    // evt.stopPropagation();
    // const confirm = (start?: string, end?: string) => {
    //   updateTransportationBenefitEffectiveDates(
    //     client!.clientBC,
    //     transportationBenefit.transportationBenefitUUID!,
    //     start,
    //     end,
    //   );
    //   setActivateDialogOpen(false);
    // };
    // const cancel = () => {
    //   setActivateDialogOpen(false);
    // };
    // setIsDeactivate(true);
    // setActivateDialogTitle(
    //   t('client.confirmDeactivateTransportationBenefitTitle'),
    // );
    // setActivateDialogMessage(
    //   t('client.confirmDeactivateTransportationBenefitMessage'),
    // );
    // setActivateDialogPostiveFn(() => confirm);
    // setActivateDialogNegativeFn(() => cancel);
    // setActivateDialogOpen(true);
  };

  const deleteTransportationBenefit = (
    evt: any,
    transportationBenefit: TransportationBenefit,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();
    const confirm = () => {
      dispatch(
        clientSlice.deleteTransportationBenefitAsync(
          client!.clientBC,
          transportationBenefit,
        ),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeleteTransportationBenefitTitle'));
    setConfirmDialogMessage(
      t('client.confirmDeleteTransportationBenefitMessage'),
    );
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const addServicePlan = () => {
    setEditingServicePlan(undefined);
    setServicePlanDialogOpen(true);
  };

  const editServicePlan = (evt: any, servicePlan: ServicePlan) => {
    evt.preventDefault();
    evt.stopPropagation();
    setEditingServicePlan(servicePlan);
    setServicePlanDialogOpen(true);
  };

  const updateServicePlanEffectiveDates = (
    clientBC: string,
    servicePlanUUID: string,
    start?: string,
    end?: string,
  ) => {
    dispatch(
      clientSlice.updateServicePlanEffectiveDatesAsync(
        clientBC,
        servicePlanUUID,
        start,
        end,
      ),
    );
  };

  const activateServicePlan = (evt: any, servicePlan: ServicePlan) => {
    evt.preventDefault();
    evt.stopPropagation();
    const confirm = (start?: string, end?: string) => {
      updateServicePlanEffectiveDates(
        client!.clientBC,
        servicePlan.servicePlanUUID!,
        start,
        end,
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(false);
    setActivateDialogTitle(t('client.confirmActivateServicePlanTitle'));
    setActivateDialogMessage(t('client.confirmActivateServicePlanMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const deactivateServicePlan = (evt: any, servicePlan: ServicePlan) => {
    evt.preventDefault();
    evt.stopPropagation();
    const confirm = (start?: string, end?: string) => {
      updateServicePlanEffectiveDates(
        client!.clientBC,
        servicePlan.servicePlanUUID!,
        start,
        end,
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(true);
    setActivateDialogTitle(t('client.confirmDeactivateServicePlanTitle'));
    setActivateDialogMessage(t('client.confirmDeactivateServicePlanMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const unassignPurposeCode = (
    servicePlan: ServicePlan,
    purposeCode: ClientPurposeCode,
  ) => {
    const confirm = () => {
      dispatch(
        clientSlice.unassignClientPurposeCodeAsync(
          client!.clientBC,
          servicePlan.servicePlanUUID!,
          purposeCode.purposeCodeUUID!,
        ),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmUnassignPurposeCodeTitle'));
    setConfirmDialogMessage(t('client.confirmUnassignPurposeCodeMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const unassignServiceArea = (
    servicePlan: ServicePlan,
    serviceArea: ServicePlanServiceArea,
  ) => {
    const confirm = () => {
      dispatch(
        clientSlice.unassignClientServiceAreaAsync(
          client!.clientBC,
          servicePlan.servicePlanUUID!,
          serviceArea.serviceAreaUUID!,
        ),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmUnassignServiceAreaTitle'));
    setConfirmDialogMessage(t('client.confirmUnassignServiceAreaMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const unassignTransportationBenefit = (
    servicePlan: ServicePlan,
    transportationBenefit: TransportationBenefit,
  ) => {
    const confirm = () => {
      dispatch(
        clientSlice.unassignTransportationBenefitAsync(
          client!.clientBC,
          servicePlan.servicePlanUUID!,
          transportationBenefit.transportationBenefitUUID!,
        ),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(
      t('client.confirmUnassignTransportationBenefitTitle'),
    );
    setConfirmDialogMessage(
      t('client.confirmUnassignTransportationBenefitMessage'),
    );
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deleteServicePlan = (evt: any, servicePlan: ServicePlan) => {
    evt.preventDefault();
    evt.stopPropagation();
    const confirm = () => {
      dispatch(
        clientSlice.deleteClientServicePlanAsync(client!.clientBC, servicePlan),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeleteServicePlanTitle'));
    setConfirmDialogMessage(t('client.confirmDeleteServicePlanMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const addProgram = () => {
    setEditingProgram(undefined);
    setProgramDialogOpen(true);
  };

  const editProgram = (evt: any, program: Program) => {
    evt.preventDefault();
    evt.stopPropagation();
    setEditingProgram(program);
    setProgramDialogOpen(true);
  };

  const updateProgramEffectiveDates = (
    clientBC: string,
    servicePlanUUID: string,
    start?: string,
    end?: string,
  ) => {
    dispatch(
      clientSlice.updateProgramEffectiveDatesAsync(
        clientBC,
        servicePlanUUID,
        start,
        end,
      ),
    );
  };

  const activateProgram = (evt: any, program: Program) => {
    evt.preventDefault();
    evt.stopPropagation();

    const confirm = (start?: string, end?: string) => {
      updateProgramEffectiveDates(
        client!.clientBC,
        program.programUUID!,
        start,
        end,
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(false);
    setActivateDialogTitle(t('client.confirmActivateProgramTitle'));
    setActivateDialogMessage(t('client.confirmActivateProgramMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const deactivateProgram = (evt: any, program: Program) => {
    evt.preventDefault();
    evt.stopPropagation();

    const confirm = (start?: string, end?: string) => {
      updateProgramEffectiveDates(
        client!.clientBC,
        program.programUUID!,
        start,
        end,
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(true);
    setActivateDialogTitle(t('client.confirmDeactivateProgramTitle'));
    setActivateDialogMessage(t('client.confirmDeactivateProgramMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const deleteProgram = (evt: any, program: Program) => {
    evt.preventDefault();
    evt.stopPropagation();

    const confirm = () => {
      dispatch(
        clientSlice.deleteClientProgramAsync(client!.clientBC, program!),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeleteProgramTitle'));
    setConfirmDialogMessage(t('client.confirmDeleteProgramMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const closeClientProfileDialog = () => {
    setClientProfileDialogOpen(false);
  };

  const closeAddressDialog = () => {
    setAddressDialogOpen(false);
  };

  const closePurposeCodeDialog = () => {
    setPurposeCodeDialogOpen(false);
  };

  const closeServiceAreaDialog = () => {
    setServiceAreaDialogOpen(false);
  };

  const closeTransportationBenefitDialog = () => {
    setTransportationBenefitDialogOpen(false);
  };

  const closeServicePlanDialog = () => {
    setServicePlanDialogOpen(false);
  };

  const closeProgramDialog = () => {
    setProgramDialogOpen(false);
  };

  const onStartDragPurposeCode = useCallback(
    (purposeCode: ClientPurposeCode) => {
      setDraggedPurposeCode(purposeCode);
    },
    [],
  );

  const onEndDragPurposeCode = useCallback((purposeCode: ClientPurposeCode) => {
    setDraggedPurposeCode(undefined);
  }, []);

  const onStartDragServiceArea = useCallback(
    (serviceArea: ServicePlanServiceArea) => {
      setDraggedServiceArea(serviceArea);
    },
    [],
  );

  const onEndDragServiceArea = useCallback(
    (serviceArea: ServicePlanServiceArea) => {
      setDraggedServiceArea(undefined);
    },
    [],
  );

  const onStartDragTransportationBenefit = useCallback(
    (transportationBenefit: TransportationBenefit) => {
      setDraggedTransportationBenefit(transportationBenefit);
    },
    [],
  );

  const onEndDragTransportationBenefit = useCallback(
    (transportationBenefit: TransportationBenefit) => {
      setDraggedTransportationBenefit(undefined);
    },
    [],
  );

  const onStartDragServicePlan = useCallback((servicePlan: ServicePlan) => {
    setDraggedServicePlan(servicePlan);
  }, []);

  const onEndDragServicePlan = useCallback((servicePlan: ServicePlan) => {
    setDraggedServicePlan(undefined);
  }, []);

  const getSelectedServicePlans = (): ServicePlan[] => {
    const selected: ServicePlan[] = [];
    Object.keys(servicePlanSelections).forEach((spId) => {
      const sp = servicePlanList?.servicePlans.find(
        (sp) => sp.servicePlanUUID === spId,
      );
      if (sp && servicePlanSelections[sp.servicePlanUUID!]) {
        selected.push(sp);
      }
    });
    return selected;
  };

  const onDragEnterServicePlan = useCallback(
    (evt: any, servicePlan: ServicePlan) => {
      if (
        draggedPurposeCode ||
        draggedServiceArea ||
        draggedTransportationBenefit
      ) {
        evt.preventDefault();
        setDroppedServicePlans(getSelectedServicePlans());
      }
    },
    [draggedPurposeCode, draggedServiceArea, draggedTransportationBenefit],
  );

  const onDragLeaveServicePlan = useCallback(
    (servicePlan: ServicePlan) => {
      setDroppedServicePlans([]);
    },
    [draggedPurposeCode, draggedServiceArea, draggedTransportationBenefit],
  );

  const assignToServicePlans = (
    servicePlans: ServicePlan[],
    thunk: Function,
    uuid: string,
  ) => {
    const clientBC = client?.clientBC!;
    servicePlans.forEach((sp) => {
      if (servicePlanSelections[sp.servicePlanUUID!]) {
        dispatch(thunk(clientBC, uuid, sp.servicePlanUUID));
      }
    });
  };

  const onDropOnServicePlan = useCallback(
    (servicePlan: ServicePlan) => {
      const servicePlans = getSelectedServicePlans();
      if (!servicePlans.length) {
        return;
      }
      setDroppedServicePlans([]);
      if (client && draggedPurposeCode && servicePlan) {
        assignToServicePlans(
          servicePlans,
          clientSlice.assignClientPurposeCodeAsync,
          draggedPurposeCode.purposeCodeUUID!,
        );
      } else if (client && draggedServiceArea && servicePlan) {
        assignToServicePlans(
          servicePlans,
          clientSlice.assignClientServiceAreaAsync,
          draggedServiceArea.serviceAreaUUID!,
        );
      } else if (client && draggedTransportationBenefit && servicePlan) {
        assignToServicePlans(
          servicePlans,
          clientSlice.assignTransportationBenefitAsync,
          draggedTransportationBenefit.transportationBenefitUUID!,
        );
      }
    },
    [draggedPurposeCode, draggedServiceArea, draggedTransportationBenefit],
  );

  const onDragEnterProgram = useCallback(
    (evt: any, program: Program) => {
      const droppable =
        program.state === 'draft' || program.state === 'inactive';
      if (draggedServicePlan && droppable) {
        evt.preventDefault();
        setDroppedProgram(program);
      }
    },
    [draggedServicePlan],
  );

  const onDragLeaveProgram = useCallback(
    (evt: any, program: Program) => {
      setDroppedProgram(undefined);
    },
    [draggedServicePlan],
  );

  const onDropOnProgram = useCallback(
    (program: Program) => {
      setDroppedProgram(undefined);
      if (client && draggedServicePlan && program) {
        dispatch(
          clientSlice.reassignClientServicePlanAsync(
            client.clientBC!,
            draggedServicePlan.servicePlanUUID!,
            program.programUUID!,
          ),
        );
      }
    },
    [draggedServicePlan],
  );

  useEffect(() => {
    if (client?.clientBC && clientBC.current !== client?.clientBC) {
      clientBC.current = client?.clientBC;
      dispatch(clientSlice.fetchClientAddressesAsync(client.clientBC));
      dispatch(clientSlice.fetchClientContactsAsync(client.clientBC));
      dispatch(clientSlice.fetchClientPurposeCodesAsync(client.clientBC));
      dispatch(clientSlice.fetchClientServiceAreasAsync(client.clientBC));
      dispatch(clientSlice.fetchTransportationBenefitsAsync(client.clientBC));
      dispatch(accountingSlice.fetchPricingModelsAsync());
      dispatch(clientSlice.fetchClientServicePlansAsync(client.clientBC));
      dispatch(clientSlice.fetchClientProgramsAsync(client.clientBC));
      dispatch(clientSlice.fetchClientSettingsAsync(client.clientBC));
      dispatch(lookupSlice.fetchLookupItemsAsync('chargeType'));
      dispatch(lookupSlice.fetchLookupItemsAsync('pricingCalculationGroup'));
      dispatch(accountingSlice.fetchPricingFunctionsAsync());
      dispatch(clientSlice.fetchTransportationBenefitModelsAsync());
      dispatch(clientSlice.fetchTransportationBenefitFunctionsAsync());
      dispatch(lookupSlice.fetchLookupItemsAsync('benefitPurpose'));
      dispatch(lookupSlice.fetchLookupItemsAsync('lineOfBusiness'));
    }
  }, [client?.clientBC]);

  useEffect(() => {
    if (userCategoryTypeIDs.length > 0) {
      dispatch(fetchUserRolesAsync({ categoryId: userCategoryTypeIDs[0] }));
    }
  }, [userCategoryTypeIDs]);

  useEffect(() => {
    if (userRoleIDs.length > 0 && client?.clientBC) {
      dispatch(
        clientSlice.fetchClientAdminsAsync(
          client.clientBC,
          adminUserPage,
          5,
          userRoleIDs,
        ),
      );
    }
  }, [userRoleIDs, client?.clientBC, adminUserPage]);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('userCategoryType'));
  }, []);

  return (
    <StyledBox elevation={0} className={classes.container}>
      <Tabs
        indicatorColor="primary"
        textColor="primary"
        value={tabIndex}
        onChange={handleTabChange}
        variant="fullWidth">
        <Tab label={t('client.profile')} data-testid="tab-one" />
        <Tab label={t('client.programs')} data-testid="tab-two" />
        <Tab label={t('client.pricing')} data-testid="tab-three" />
        {/* <Tab label={t('client.benefits')} data-testid="tab-four" /> */}
        <Tab label={t('client.locations')} data-testid="tab-four" />
        {/* The Preferences tab is removed for now */}
        {/* <Tab label={t('client.preferences')} data-testid="tab-six" /> */}
      </Tabs>
      <TabPanel value={tabIndex} index={0}>
        <Box className={classes.tab}>
          <ClientProfile
            client={client}
            edit={editProfile}
            activate={activateClient}
            deactivate={deactivateClient}
            readonly={readonly}
          />
          <ClientAddresses
            clientAddresses={client ? addresses : undefined}
            addAddress={addAddress}
            editAddress={editAddress}
            activateAddress={activateAddress}
            deactivateAddress={deactivateAddress}
            deleteAddress={deleteAddress}
            readonly={readonly}
          />
          <ClientContacts
            clientContacts={client ? contacts : undefined}
            addContact={addContact}
            editContact={editContact}
            activateContact={activateContact}
            deactivateContact={deactivateContact}
            deleteContact={deleteContact}
            readonly={readonly}
          />
          <ClientAdmins
            clientAdmins={admins}
            changePage={(p) => setAdminUserPage(p)}
            addAdmin={addAdmin}
            resendInvite={resendInvite}
            deleteAdmin={deleteAdmin}
            readonly={readonly}
          />
        </Box>
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        <Box className={classes.tab}>
          {!readonly && (
            <ClientPurposeCodes
              client={client}
              purposeCodes={purposeCodes}
              onAdd={addPurposeCode}
              onActivate={activatePurposeCode}
              onDeactivate={deactivatePurposeCode}
              onDelete={deletePurposeCode}
              onStartDrag={onStartDragPurposeCode}
              onEndDrag={onEndDragPurposeCode}
            />
          )}
          {!readonly && (
            <ClientServiceAreasDeprecated
              client={client}
              serviceAreas={serviceAreas}
              addServiceArea={addServiceArea}
              onActivate={activateServiceArea}
              onDeactivate={deactivateServiceArea}
              onEdit={editServiceArea}
              onDelete={deleteServiceArea}
              onStartDrag={onStartDragServiceArea}
              onEndDrag={onEndDragServiceArea}
            />
          )}
          {!readonly && (
            <ClientTransportationBenefits
              client={client}
              transportationBenefits={transportationBenefits}
              onAdd={addTransportationBenefit}
              onActivate={activateTransportationBenefit}
              onDeactivate={deactivateTransportationBenefit}
              onEdit={editTransportationBenefit}
              onDelete={deleteTransportationBenefit}
              onStartDrag={onStartDragTransportationBenefit}
              onEndDrag={onEndDragTransportationBenefit}
            />
          )}
          {!readonly && (
            <ClientServicePlans
              client={client}
              servicePlans={servicePlanList?.servicePlans || []}
              continuationToken={servicePlanList?.continuationToken}
              fetchMoreServicePlans={fetchMoreServicePlans}
              purposeCodes={purposeCodes}
              serviceAreas={serviceAreas}
              transportationBenefits={transportationBenefits}
              servicePlanSelections={servicePlanSelections}
              setServicePlanSelections={setServicePlanSelections}
              onAdd={addServicePlan}
              onEdit={editServicePlan}
              onActivate={activateServicePlan}
              onDeactivate={deactivateServicePlan}
              onDelete={deleteServicePlan}
              onUnassignPurposeCode={unassignPurposeCode}
              onUnassignServiceArea={unassignServiceArea}
              onUnassignTransportationBenefit={unassignTransportationBenefit}
              onStartDrag={onStartDragServicePlan}
              onEndDrag={onEndDragServicePlan}
              onDragEnter={onDragEnterServicePlan}
              onDragLeave={onDragLeaveServicePlan}
              onDrop={onDropOnServicePlan}
              dropServicePlanUUIDs={
                draggedPurposeCode ||
                draggedServiceArea ||
                draggedTransportationBenefit
                  ? droppedServicePlans.map((sp) => sp.servicePlanUUID!)
                  : []
              }
            />
          )}
          {!readonly && (
            <ClientPrograms
              client={client}
              programs={programs}
              servicePlans={servicePlanList?.servicePlans || []}
              purposeCodes={purposeCodes}
              serviceAreas={serviceAreas}
              transportationBenefits={transportationBenefits}
              addProgram={addProgram}
              onEdit={editProgram}
              onActivate={activateProgram}
              onDeactivate={deactivateProgram}
              onDelete={deleteProgram}
              onDragEnter={onDragEnterProgram}
              onDragLeave={onDragLeaveProgram}
              onDrop={onDropOnProgram}
              dropProgramUUID={
                draggedServicePlan && droppedProgram
                  ? droppedProgram.programUUID
                  : undefined
              }
            />
          )}
        </Box>
      </TabPanel>
      <TabPanel value={tabIndex} index={2}>
        <Box className={classes.tab}>
          {client && (
            <ClientPricing
              clientBC={client.clientBC}
              servicePlans={servicePlanList?.servicePlans || []}
              pricingModels={pricingModels}
            />
          )}
        </Box>
      </TabPanel>
      <TabPanel value={tabIndex} index={3}>
        <Box className={classes.tab}>
          {client && <ClientLocations client={client} />}
        </Box>
      </TabPanel>
      <TabPanel value={tabIndex} index={5}>
        <Box className={classes.tab}>
          {client && (
            <ClientPreferences
              clientBC={client.clientBC}
              preferences={preferences}
            />
          )}
        </Box>
      </TabPanel>
      {client && (
        <>
          <ClientProfileDialog
            title={t('client.editProfile')}
            client={client!}
            open={clientProfileDialogOpen}
            close={closeClientProfileDialog}
          />
          <AddressDialog
            title={
              editingAddress ? t('client.editAddress') : t('client.addAddress')
            }
            open={addressDialogOpen}
            client={client}
            address={editingAddress}
            close={closeAddressDialog}
          />
          <ContactDialog
            title={
              editingContact ? t('client.editContact') : t('client.addContact')
            }
            open={contactDialogOpen}
            client={client}
            contact={editingContact}
            close={closeContactDialog}
          />
          <AdminDialog
            title={t('client.addAdmin')}
            open={adminDialogOpen}
            client={client}
            close={closeAdminDialog}
            userRoleIDs={userRoleIDs}
            userCategoryTypeIDs={userCategoryTypeIDs}
          />
          <PurposeCodeDialog
            clientBC={client.clientBC}
            open={purposeCodeDialogOpen}
            close={closePurposeCodeDialog}
          />
          <ServiceAreaDialog
            title={
              editingServiceArea
                ? t('client.editServiceArea')
                : t('client.addServiceArea')
            }
            clientBC={client.clientBC}
            serviceArea={editingServiceArea}
            open={serviceAreaDialogOpen}
            close={closeServiceAreaDialog}
          />
          <TransportationBenefitDialog
            title={
              editingTransportationBenefit
                ? t('client.editTransportationBenefit')
                : t('client.addTransportationBenefit')
            }
            clientBC={client.clientBC}
            transportationBenefit={editingTransportationBenefit}
            open={transportationBenefitDialogOpen}
            close={closeTransportationBenefitDialog}
          />
          <ServicePlanDialog
            title={
              editingServicePlan
                ? t('client.editServicePlan')
                : t('client.addServicePlan')
            }
            open={servicePlanDialogOpen}
            client={client!}
            programs={programs}
            servicePlan={editingServicePlan}
            close={closeServicePlanDialog}
          />
          <ProgramDialog
            title={
              editingProgram ? t('client.editProgram') : t('client.addProgram')
            }
            open={programDialogOpen}
            client={client}
            program={editingProgram}
            close={closeProgramDialog}
          />
          <ConfirmationDialog
            title={confirmDialogTitle}
            message={confirmDialogMessage}
            open={confirmDialogOpen}
            positiveAction={confirmDialogPostiveFn}
            negativeAction={confirmDialogNegativeFn}
          />
          <ActivateDialog
            title={activateDialogTitle}
            message={activateDialogMessage}
            open={activateDialogOpen}
            forActivate={!isDeactivate}
            handleOk={activateDialogPostiveFn}
            handleCancel={activateDialogNegativeFn}
          />
        </>
      )}
    </StyledBox>
  );
}
